import { isEmpty } from "lodash-es";
import { useState } from "react";
import { useApiServiceCallback } from "../../../server/api/client";
import { useSupabase } from "../../../server/supabase/hooks";
import { toISO8601 } from "../../../utils/iso8601";
import type { ChannelName } from "../../channels/types";
import { SlackChannelService } from "../../segment/client/slack";
import {
  type RecipientPerson,
  type RecipientPersonGroup,
  SupabaseRecipientService,
} from "../server/supabase-recipient-service";

const filterChannelsToUpdate = (channels: RecipientPerson[]): RecipientPerson[] => {
  const sevenDaysAgo: Date = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  return channels.filter(
    (channel) =>
      !channel.postability_check_at || new Date(channel.postability_check_at) < sevenDaysAgo,
  );
};

export const useChannels = (
  service: ChannelName,
): {
  channels: RecipientPersonGroup[];
  loading: boolean;
  check: (checkChannels: RecipientPerson[]) => Promise<void>;
} => {
  const [channels, setChannels] = useState<RecipientPersonGroup[]>([]);
  const [loading, setLoading] = useState<{ channels: boolean; check: boolean }>({
    channels: true,
    check: false,
  });

  const check = useApiServiceCallback(
    SlackChannelService,
    async ({ apiService }, checkChannels: RecipientPerson[]) => {
      if (service !== "slack") return;

      setLoading((prev) => ({ ...prev, check: true }));

      const channelsToUpdate = filterChannelsToUpdate(checkChannels);

      if (channelsToUpdate.length === 0) return setLoading((prev) => ({ ...prev, check: false }));

      const resp = await apiService.checkSlackChannels(channelsToUpdate.map(({ id }) => id));

      const data = resp.data || {};

      if (isEmpty(data)) return;

      setChannels((prev) =>
        prev.map((channel) => {
          return data !== undefined && data[channel.id] !== undefined
            ? {
                ...channel,
                postability: data[channel.id]!,
                postability_check_at: toISO8601(new Date()),
              }
            : channel;
        }),
      );

      setLoading((prev) => ({ ...prev, check: false }));
    },
    [service],
  );

  useSupabase(
    async ({ supabase, account_id }) => {
      if (loading.channels) {
        const { data } = service
          ? await new SupabaseRecipientService(supabase).getForChannel(account_id, service, true)
          : { data: [] };

        if (data) setChannels(data);
      }
      setLoading((prev) => ({ ...prev, channels: false }));
    },
    [loading.channels, service],
  );

  return {
    channels,
    loading: loading.channels || loading.check,
    check,
  };
};
